/**
 * boreal-idle-load.js
 *
 * Defers non-critical, INTERACTION-ONLY sitewide scripts out of the page-load
 * TBT window. The script it loads only does real work once the user opens a
 * dialog/drawer, so there is no reason to parse + evaluate it during load:
 *
 *   - tm-size-guide.js             → size-guide drawer (opens on click)
 *   - back-in-stock element script → "Prévenez-moi" drawer (opens on click; must
 *                                    be sitewide because quick-add injects it)
 *
 * Strategy: load on `requestIdleCallback` after `window.load`, OR on the first
 * user interaction (pointerdown / keydown / touchstart), whichever comes first.
 * Idle keeps them off the critical path; the interaction fallback guarantees
 * they are requested the moment the visitor starts engaging with the page.
 *
 * Asset URLs are passed in via data-* attributes (no hardcoded paths, so the
 * Shopify CDN fingerprint stays correct). Loaded itself via a single deferred
 * <script> tag in snippets/scripts.liquid.
 */
(() => {
  const loader =
    document.currentScript || document.querySelector('script[data-boreal-idle-load]');
  if (!loader) return;

  const quickAddSrc = loader.dataset.quickAdd;
  const sizeGuideSrc = loader.dataset.sizeGuide;
  const backInStockSrc = loader.dataset.backInStock;

  let booted = false;
  const events = ['pointerdown', 'keydown', 'touchstart'];

  const cleanup = () => {
    events.forEach((type) => window.removeEventListener(type, boot, true));
  };

  function boot() {
    if (booted) return;
    booted = true;
    cleanup();

    if (quickAddSrc) {
      const q = document.createElement('script');
      q.src = quickAddSrc;
      q.defer = true;
      document.head.appendChild(q);
    }

    if (sizeGuideSrc) {
      const s = document.createElement('script');
      s.src = sizeGuideSrc;
      s.type = 'module';
      document.head.appendChild(s);
    }

    if (backInStockSrc) {
      const b = document.createElement('script');
      b.src = backInStockSrc;
      b.defer = true;
      document.head.appendChild(b);
    }
  }

  // First-interaction fallback (capture phase so we react as early as possible).
  events.forEach((type) =>
    window.addEventListener(type, boot, { capture: true, once: true, passive: true })
  );

  const schedule = () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(boot, { timeout: 4000 });
    } else {
      setTimeout(boot, 2000);
    }
  };

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
})();
